"use client";

import CategoryReviewCard from "@packages/shared-components/common-utilities/modal/review-lightbox/common/CategoryReviewCard";
import { useState } from "react";
import Image from "next/image";
import HelpfulButton from "./helpful-button";
import { getProjectSpecificData } from "@packages/lib/utlils/commonFunction";
import makeApiCall from "@packages/lib/server-actions/rest-api";
import API_URL from "@packages/lib/server-actions/api-urls";
import { getCookie } from "@packages/lib/utlils/cookie-utils";
import DetailedReviewLightBox from "../common-utilities/modal/review-lightbox/DetailedReviewLightBox";
import {
  GA4DataLayer,
  GA4DataLayerFn,
} from "@packages/lib/utlils/helper-function";
import { useSearchParams } from "next/navigation";
import { GAConstants } from "../services/constants";

const DetailedReviewContents = ({
  idx,
  review,
  commonGAAttrs,
  commonGAAttrsOld,
}: any) => {
  let [isReviewExpand, setIsReviewExpand] = useState(idx === 0);
  let [currentLikesCount, setCurrentLikesCount] = useState(
    review?.likesCount || 0,
  );
  const [reviewData, setReviewData] = useState<any[] | null>(null);

  const scrollToElement = (elementId: string) => {
    const element = document.getElementById(elementId);
    element?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const helpfulAnonymousCall = async (
    reviewId: number,
    reviewUuid: number,
    action: string,
  ) => {
    try {
      const authToken = getCookie("authToken");
      const headers: any = {
        "x-api-key": process.env.NEXT_PUBLIC_X_API_KEY!,
        trackSessionId: getCookie("trackSessionId") || "",
      };

      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }

      const helpfulResponse = await makeApiCall({
        url: `${process.env.NEXT_PUBLIC_BFF_API_DOMAIN}${API_URL?.helpfulReview}${reviewId}`,
        method: "POST",
        headers,
        body: {
          reviewId: reviewId,
          reviewUuid: reviewUuid,
          action: action.toUpperCase(),
          appFlag: "N",
          affiliateId: getProjectSpecificData("affiliateId"),
          networkId: 2,
        },
      });
      return {
        success: helpfulResponse?.status === 200,
        data: helpfulResponse,
      };
    } catch (error) {
      return { success: false, error };
    }
  };

  function toggleReadMoreLess() {
    setIsReviewExpand((prev: boolean) => {
      if (prev) {
        scrollToElement("user-review-body-" + idx);
      }
      return !prev;
    });
  }

  const handleReviewLightBox = (reviewData: any) => {
    setReviewData(() => ({ ...reviewData }));
  };

  const searchParams = useSearchParams();

  const sendReviewGA = (label2: string, label3?: string) => {
    GA4DataLayerFn({
      ...commonGAAttrs,
      event: "ga_events",
      eventName: "ai_summarised_reviews",
      data_label: "review_content_detailed_click",
      data_label2: label2,
      data_label3: label3,
      data_label4: review?.reviewerName,
      data_label5: `review_number_${idx + 1}`,
      study_level: searchParams.get("studyLevel") || "undergraduate",
    });
  };

  const handleUniversityNameGA = () =>
    sendReviewGA("university_name_clicked", review?.institutionDisplayName);

  const handleCourseNameGA = () => {
    //sendReviewGA("course_name_clicked", review?.courseDisplayName);
    GA4DataLayer({
      ...commonGAAttrs,
      event: GAConstants.track_event,
      event_name: "course_click",
      subject_area: review?.courseDisplayName,
    });
    GA4DataLayerFn({
      ...commonGAAttrsOld,
      event: GAConstants.review_events,
      eventName: "cdlinkclicks_cdpage",
    });
  };

  const handleViewPhotosGA = () =>
    sendReviewGA(
      "view_photos_clicked",
      `+${review.imageDetails.length - 3} Photos`,
    );

  const handleLikesGA = (likesCount: number) => {
    //sendReviewGA("likes_clicked", likesCount.toString());
    GA4DataLayer({
      ...commonGAAttrs,
      event: GAConstants.track_event,
      event_name: "user_interaction",
      subject_area: review?.courseDisplayName,
    });
  };

  return (
    <>
      <div
        id={"user-review-body-" + idx}
        className="review-gallery-scroll flex flex-1 md:px-[0] max-md:mr-[16px] max-md:custom-scrollbar-2 max-md:overflow-y-auto max-md:h-[calc(100vh-212px)] lg:custom-scrollbar-2 h-auto "
      >
        <div className="review-comments-card">
          <div className="review-comments flex flex-col gap-[16px]">
            <div className="review-comments__col w-full">
              <div className="flex items-center gap-[16px]">
                <div className="reviewer__avatar flex items-center justify-center min-w-[40px] w-[40px] h-[40px] md:w-[80px] md:h-[80px] bg-primary-400 md:text-heading4 font-bold text-grey-50 uppercase rounded-[80px]">
                  {review?.initials}
                </div>
                <div className="flex flex-col flex-1 *:text-x-small *:text-grey300 *:text-left">
                  <div className="font-semibold break-words break-all">
                    {review?.reviewerName}
                  </div>
                  <p>{review?.ageGroup}</p>
                  <p>{review?.isFirstGenStudent ? "First gen student" : ""}</p>
                </div>
              </div>
            </div>
            <div className="review-comments__col flex-1">
              <div className="flex flex-col flex-1 gap-[24px]">
                <div className="flex flex-col flex-1 *:text-grey300">
                  <div className="flex flex-col gap-[4px]">
                    <div
                      className="para font-semibold"
                      onClick={handleUniversityNameGA}
                    >
                      {review?.institutionDisplayName}
                    </div>
                    <div className="x-small">
                      Reviewed:{" "}
                      {review?.reviewedDate ? `${review.reviewedDate}*` : ""}
                    </div>
                    {review?.courseDeletedFlag === "N" ? (
                      <a
                        className="small text-primary-400 hover:text-primary-500 hover:underline"
                        href={`/degrees/${review?.courseTextKey}/${review?.institutionTextKey}/cd/${review?.courseId}/${review?.institutionId}`}
                        onClick={handleCourseNameGA}
                      >
                        {review?.courseDisplayName}
                      </a>
                    ) : (
                      <span className="small text-primary-400">
                        {review?.courseDisplayName}
                      </span>
                    )}
                    <div className="flex gap-2">
                      {review?.imageDetails
                        ?.slice(0, 3)
                        ?.map((img: any, idx: number) => (
                          <div
                            key={idx}
                            className="relative cursor-pointer"
                            onClick={() => {
                              handleViewPhotosGA();
                              handleReviewLightBox(review);
                            }}
                          >
                            <Image
                              className="h-[112px] object-cover object-center rounded-[4px]"
                              src={`${process.env.NEXT_PUBLIC_IMAGE_DOMAIN}${img.mediaPath}`}
                              alt={img.imageTags || "review image"}
                              width={174}
                              height={112}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                            {idx === 2 && review?.imageDetails?.length > 3 && (
                              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-[4px] flex items-center justify-center">
                                <span className="text-white font-semibold">
                                  +{review.imageDetails.length - 3} Photos
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
                <div className="review-body flex flex-col gap-[16px]">
                  <div className="flex flex-col gap-[20px]">
                    {(isReviewExpand
                      ? review?.categoryComments
                      : review?.categoryComments?.slice(0, 1)
                    )?.map((catReview: any, idx: number) => (
                      <div key={idx}>
                        <CategoryReviewCard
                          catReview={catReview}
                          hallName={review?.hallName}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                {isReviewExpand && review?.categoryComments?.length > 0 && (
                  <div className="flex flex-row items-center gap-[4px]">
                    <Image
                      src="/static/assets/icons/reward_icon.svg"
                      alt="Reward icon"
                      width={20}
                      height={20}
                    />
                    <span className="text-grey300 text-sm font-normal font-inter italic">
                      *Students got a small reward for their time
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div
              className="inline-flex items-center gap-[4px] cursor-pointer"
              onClick={toggleReadMoreLess}
            >
              <svg
                className={`${isReviewExpand ? "rotate-180" : ""}`}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 6L8 10L12 6"
                  stroke="#4664DC"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="justify-start text-primary-400 text-sm font-normal font-inter leading-[17px] hover:underline hover:decoration-primary-400">
                {isReviewExpand ? "Read less" : "Read more"}
              </div>
            </div>
            <div className="font-inter">
              <HelpfulButton
                reviewId={
                  review?.reviewId || `review_${review?.institutionId}_${idx}`
                }
                initialLikesCount={currentLikesCount}
                onLikesCountChange={setCurrentLikesCount}
                onHelpfulClick={async (action) => {
                  handleLikesGA(currentLikesCount);
                  return await helpfulAnonymousCall(
                    review?.reviewId,
                    review?.reviewUuid,
                    action,
                  );
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {reviewData && (
        <DetailedReviewLightBox
          reviewId={review?.reviewId || null}
          reviewData={reviewData}
          noVideoFlag={true}
          lightBoxType={"galleryView"}
          closeModal={() => setReviewData(null)}
        />
      )}
    </>
  );
};

export default DetailedReviewContents;
